import {
  IconHeart,
  IconSun,
  IconClock,
  IconCoin,
  IconShieldLock,
  IconChartDots,
} from "@tabler/icons-react";
import type { ToolLandingConfig } from "../pages/ToolLandingPage";

/* Meta and long-form copy for the homepage-shaped tool landing pages.

   The HERO heading and the FAQ heading match the homepage on purpose. The copy
   below is deliberately unique per page: these two were 96% identical to each
   other and 93% identical to the homepage, which is the shape Google filters
   rather than ranks. Everything here is written for the one question the page
   is bought to answer. */

export const loveLanding: ToolLandingConfig = {
  url: "https://astroaura.ai/love/",
  headingLead: "Love Compatibility",
  headingAccent: "Calculator",
  title: "Love Compatibility Calculator, Free | Aura AI",
  description:
    "Check your love compatibility free, then ask an AI Vedic astrologer about the relationship, timing and what your charts actually say. Free to start, 24x7.",
  toolHeading: "Love Compatibility: See Where You Two Stand",
  toolSubheading:
    "Two names is all it takes to start. Ask Aura AI afterwards and it reads the answer against your actual birth charts.",
  toolIcon: <IconHeart size={56} stroke={1.4} />,
  faqHeading: "Free AI Astrology Chat: Your Questions Answered",
  faqSubheading:
    "What is free, how the AI reads your birth chart, and what it costs after that",
  sections: [
    {
      heading: "A number is a starting point, not an answer",
      paragraphs: [
        "The name-based score above is the fun part. It is quick, it is a conversation starter, and it is not astrology. We say that plainly rather than dressing it up, because a percentage built from two names cannot know anything about either of you.",
        "What the score is good for is getting the actual question out of you. Almost nobody types two names because they are curious about arithmetic. They type them because something specific is on their mind: whether the timing is wrong, whether the families will agree, whether the distance is survivable, whether to wait.",
        "That question is the one worth asking Aura AI, and that one is answered from your charts rather than your spelling.",
      ],
    },
    {
      heading: "What Vedic compatibility actually looks at",
      paragraphs: [
        "Traditional Vedic matching, gun milan, scores eight factors across 36 points, weighing things like temperament, emotional compatibility and the tension between the two charts. It is far more specific than a sun-sign grid that declares Leo and Scorpio incompatible and leaves it there.",
        "It also looks at the seventh house, which governs partnership, at where Venus and Mars sit for each person, and at the planetary periods you are both currently running. Two charts can match well and still have a difficult year, because the periods matter as much as the match does.",
        "That is usually the useful answer. Not whether you are compatible in the abstract, but whether the next eighteen months are a good window for the decision you are actually weighing.",
      ],
    },
    {
      heading: "Timing is the question people are really asking",
      paragraphs: [
        "Most relationship questions are timing questions wearing a disguise. Should we tell them now. Is this the year. Do I wait for him to be ready. Is it too soon after the last one.",
        "Vedic astrology is unusually specific here, because the dasha system divides a life into planetary chapters with dates attached. A Venus period reads very differently from a Saturn one for a question about commitment, and knowing which one you are in changes the advice rather than just the tone.",
        "Ask Aura AI with your birth details and the answer comes back against your chart, at whatever hour you happen to be awake and thinking about it.",
      ],
    },
    {
      heading: "Where sun-sign compatibility goes wrong",
      paragraphs: [
        "The familiar grid, fire signs with air signs, water with earth, Leo and Scorpio a disaster, is comparing one placement out of roughly forty in each chart. It is the astrological equivalent of judging two people's compatibility on their star sign because it is the only fact you have.",
        "Venus placement usually says more about how someone loves than their sun sign does, and Mars says more about how they argue, which is frequently the more decisive question. The seventh house describes what you are looking for in a partner, and it is often nothing like what your sun sign suggests.",
        "This is why two people who are supposedly incompatible can work easily, and two who are supposedly ideal can grind. The grid was never measuring the thing that decides it.",
      ],
    },
    {
      heading: "Difficult placements, and the honest version",
      paragraphs: [
        "You may have heard of manglik dosha, the Mars placement traditionally treated as a serious obstacle to marriage in Indian matchmaking. It is real in the sense that the placement is real, and it is routinely overstated in the sense that it is frequently cancelled by other factors in the same chart.",
        "We will tell you when something like that is present and when it is mitigated, rather than either hiding it or using it to frighten you into buying a remedy. A reading that only ever delivers good news is not a reading.",
        "The same applies to timing. If the next window looks poor, the useful answer is when the next good one opens, not a reassurance that everything is fine.",
      ],
    },
    {
      heading: "Private enough to be honest in",
      paragraphs: [
        "People ask this app things they have not said to their closest friend. That only works if the conversation stays private, so it does: encrypted in transit, never sold to advertisers or data brokers, and your birth details used for your chart and nothing else.",
        "You do not need the other person's consent, their birth time, or their involvement at all to ask about your own chart and your own decision.",
      ],
    },
  ],
  faqItems: [
    {
      question: "Is the love percentage real astrology?",
      answer:
        "No, and we will not pretend otherwise. The percentage is calculated from the two names, so it is a bit of fun rather than a reading. The astrology starts when you ask Aura AI, which answers from your actual birth chart rather than your spelling.",
      icon: <IconHeart size={22} />,
    },
    {
      question: "What does Vedic compatibility actually measure?",
      answer:
        "Traditional gun milan scores eight factors across 36 points, covering temperament, emotional fit and the friction between two charts. Beyond that it looks at the seventh house of partnership, where Venus and Mars sit for each person, and which planetary periods you are both currently running.",
      icon: <IconChartDots size={22} />,
    },
    {
      question: "Do I need my partner's birth details?",
      answer:
        "Not to start. You can ask about your own chart, your own timing and your own decision without involving them at all. A full two-chart match needs both sets of details, but most of what people want to know can be answered from one.",
      icon: <IconClock size={22} />,
    },
    {
      question: "Can it tell me when to make a decision?",
      answer:
        "That is what Vedic astrology is unusually good at. The dasha system splits a life into planetary periods with actual dates, so a question about when to commit, wait or move gets an answer tied to the chapter you are currently in rather than a general verdict.",
      icon: <IconClock size={22} />,
    },
    {
      question: "What does it cost?",
      answer:
        "The compatibility check is free. Your first question to Aura AI is free too. After that, questions are priced per message and the price is always shown before you confirm, so nothing is charged without you agreeing to it.",
      icon: <IconCoin size={22} />,
    },
    {
      question: "Is what I ask private?",
      answer:
        "Yes. Conversations are encrypted in transit and are never sold to advertisers or data brokers, and your birth details are used to compute your chart and nothing else.",
      icon: <IconShieldLock size={22} />,
    },
  ],
};

export const horoscopeLanding: ToolLandingConfig = {
  url: "https://astroaura.ai/horoscope/",
  headingLead: "Today's",
  headingAccent: "Horoscope",
  title: "Today's Horoscope, Free Daily Reading | Aura AI",
  description:
    "Read today's horoscope for your sign free, then ask an AI Vedic astrologer about love, career, money and timing from your own birth chart. Free to start, 24x7.",
  toolHeading: "Today's Horoscope: Read From Your Own Chart",
  toolSubheading:
    "Not a generic sun-sign column. Enter your birth details and the reading is built from the planets in your chart today.",
  toolIcon: <IconSun size={56} stroke={1.4} />,
  faqHeading: "Free AI Astrology Chat: Your Questions Answered",
  faqSubheading:
    "What is free, how the AI reads your birth chart, and what it costs after that",
  sections: [
    {
      heading: "Why newspaper horoscopes are the same for millions of people",
      paragraphs: [
        "A sun-sign column sorts the planet into twelve groups and writes one paragraph for each. Roughly 650 million people share your sign. No sentence can be true of all of them, so the sentences are written loose enough to feel true to anyone, which is exactly why they never tell you anything you could act on.",
        "The reading above is built differently. It takes your date, time and place of birth, computes where the planets actually were, and reads today's transits against that chart. The rising sign alone changes every house position, and rising sign changes roughly every two hours, so two people born the same day in the same city can get meaningfully different readings.",
      ],
    },
    {
      heading: "What today's reading is actually looking at",
      paragraphs: [
        "The daily reading works from the transits, where the planets are right now, against your natal positions. It weighs the Moon, which moves fastest and sets the emotional texture of a day, alongside slower contacts from Mars, Jupiter and Saturn that give the day its shape.",
        "It also accounts for which dasha period you are running. The same transit lands very differently inside a Jupiter chapter than inside a Saturn one, which is why a reading built from your chart can say a day is worth pushing on while the same transit is a warning for someone else.",
        "That is the part a twelve-paragraph column structurally cannot do.",
      ],
    },
    {
      heading: "Vedic timing, and why your sign may not be the one you think",
      paragraphs: [
        "Vedic astrology uses the sidereal zodiac, which follows the constellations where they physically sit. Western horoscopes use the tropical zodiac, anchored to the seasons. The two have drifted about 24 degrees apart over the centuries, so a Vedic chart frequently places you in the sign before the one you have called yours your whole life.",
        "If a reading here describes someone you recognise but under the wrong label, that gap is usually the reason. It is not a different opinion about you, it is a different measurement of the sky.",
        "The practical payoff is timing. Vedic technique is built around dated planetary periods, so it talks about when things turn rather than only what you are like.",
      ],
    },
    {
      heading: "Why the Moon matters more than the Sun here",
      paragraphs: [
        "Western astrology puts the Sun at the centre, which is why you are asked your sun sign. Vedic practice leans on the Moon, and daily readings especially so. The Moon changes sign roughly every two and a half days, and it governs mind and mood, which is the register a daily reading works in.",
        "That is why a Vedic daily reading is often more specific about how a day will feel than about what will happen in it. It is describing the weather you are carrying rather than events arriving from outside.",
        "It also explains why your Vedic moon sign frequently feels more like you than your sun sign does, particularly if you have never quite recognised yourself in the standard description of your Western one.",
      ],
    },
    {
      heading: "What a daily reading cannot do",
      paragraphs: [
        "It cannot tell you a lottery number, name a date something will happen, or promise an outcome. Anyone offering that is selling certainty, and certainty is the one thing astrology has never had to give.",
        "What it can do is describe the conditions. Which parts of your chart are being pressed on today, whether the pressure is short-lived or part of a longer chapter, and which decisions are better taken now than in three weeks.",
        "Read that way it is closer to a forecast than a prophecy, and considerably more useful, because a forecast is something you can plan around.",
      ],
    },
    {
      heading: "Read it once, then ask the follow-up",
      paragraphs: [
        "A daily reading is most useful when you can interrogate it. If today says a conversation will go well, the real question is which conversation, and that is what Aura AI is for.",
        "Ask in plain language, at whatever hour, and the answer comes back from the same chart the reading was built from. Your birth details are used for that and nothing else, and conversations are encrypted in transit and never sold on.",
      ],
    },
  ],
  faqItems: [
    {
      question: "How is this different from a normal daily horoscope?",
      answer:
        "A sun-sign column writes twelve paragraphs for the whole planet, so around 650 million people read the same one. This reading is computed from your date, time and place of birth, so it reflects your rising sign, your house positions and the planetary period you are actually in.",
      icon: <IconSun size={22} />,
    },
    {
      question: "Why do I need my birth time for a daily reading?",
      answer:
        "Your rising sign changes roughly every two hours and it sets every house position in the chart. Without a birth time the reading falls back to broader indicators. An hour out can genuinely change the answer, so use the closest time you know and treat the specifics as approximate.",
      icon: <IconClock size={22} />,
    },
    {
      question: "Why is my Vedic sign different from my usual one?",
      answer:
        "Vedic astrology uses the sidereal zodiac, which tracks the constellations where they actually are, while Western horoscopes use the tropical zodiac fixed to the seasons. They have drifted about 24 degrees apart, so your Vedic sign is often the one before your Western one.",
      icon: <IconChartDots size={22} />,
    },
    {
      question: "Is the daily reading free?",
      answer:
        "Yes, and you can read it as often as you like. Your first question to Aura AI is free as well. After that, questions are priced per message with the price shown before you confirm.",
      icon: <IconCoin size={22} />,
    },
    {
      question: "Can I ask about something the reading mentions?",
      answer:
        "That is the point of it. Ask in plain language and Aura AI answers from the same chart the reading was built from, so the follow-up is specific to you rather than to your sign.",
      icon: <IconSun size={22} />,
    },
    {
      question: "What happens to my birth details?",
      answer:
        "They are used to compute your chart and nothing else. Conversations are encrypted in transit and are never sold to advertisers or data brokers.",
      icon: <IconShieldLock size={22} />,
    },
  ],
};
